'use client';
import { Phone } from 'lucide-react';
import type { VoiceCallRecord } from '../../lib/types';
import { useTranslation } from '../../lib/i18n';
import { formatDuration } from '../../lib/format';
import { latestVoiceOperations } from '../../lib/voiceCallPresentation';
import { EmptyState } from '../../components/ui/states';

/** Plain-text entries of a saved call. */
function VoiceTranscriptEntries({ transcript }: { transcript: VoiceCallRecord['transcript'] }) {
  const { t } = useTranslation();
  const s = t.voice.transcript;
  return transcript.length ? <ol className="mt-2 space-y-2">
    {transcript.map((entry, index) => <li key={index} className="min-w-0">
      <span className="text-emphasis opacity-80">{entry.role === 'user' ? s.user : s.assistant}</span>
      <p className="whitespace-pre-wrap break-words text-foreground">{entry.text}</p>
    </li>)}
  </ol> : <EmptyState title={s.empty} />;
}

/** One projected call: its header, admitted summary, collapsed transcript and current operation rows.
 *  The shared presentation projection owns lifecycle coalescing. Typed metadata is rendered as plain text;
 *  neither the summary nor the transcript is parsed as markdown. Secondary text keeps the bubble's own ink
 *  at reduced opacity: a muted document colour is illegible on a skin's coloured user bubble. */
export function VoiceCallMessage({ call, text, full }: { call: VoiceCallRecord; text: string; full?: boolean }) {
  const { t } = useTranslation();
  const s = t.voice.transcript;
  return (
    <div data-testid="voice-call-message" className={`min-w-0 ${full ? '' : 'my-1.5'}`}>
      <div className="flex items-center gap-1.5 text-small opacity-80">
        <Phone size={12} aria-hidden />
        <span>{s.call} · {formatDuration(call.durationMs)}</span>
      </div>
      {text.trim() ? <div className="mt-1 whitespace-pre-wrap text-conversation text-foreground">{text}</div> : null}
      {call.transcript.length || !call.operations?.length ? <details className="mt-1.5 min-w-0 text-ui">
        <summary className="cursor-pointer rounded-sm opacity-80 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {s.title}
        </summary>
        <VoiceTranscriptEntries transcript={call.transcript} />
      </details> : null}
      {latestVoiceOperations(call.operations ?? []).map(operation => <details key={operation.id} data-testid="voice-call-operation" className="mt-1.5 min-w-0 text-ui">
        <summary className="cursor-pointer rounded-sm break-words hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span className="text-emphasis">{t.voice.operations.status[operation.status]}</span>
          {' · '}<span className="whitespace-pre-wrap">{operation.summary}</span>
        </summary>
        <dl className="mt-2 space-y-1 break-words">
          <div><dt className="inline opacity-80">{t.voice.operations.kind}: </dt><dd className="inline">{t.voice.operations.kinds[operation.kind]}</dd></div>
          {operation.reason ? <div><dt className="inline opacity-80">{t.voice.operations.reason}: </dt><dd className="inline">{t.voice.operations.reasons[operation.reason]}</dd></div> : null}
        </dl>
      </details>)}
    </div>
  );
}
